import React, { useRef, useState } from "react";
import { Formik } from "formik";
import CMSFormTextArea from "../../formFields/CMSFormTextArea";
import ContainerLoading from "../../common/loading/ContainerLoading";
import { useToast } from "../../contexts/ToastContext";
import { dateChangeSchema } from "../../utils/validations";
import Modal from "../../common/Modal";
import Icon from "../../common/Icon";
import mealImage from "../../../assets/images/mealImage.png";
import { useNavigate } from "react-router-dom";
import { FiArrowLeft } from "react-icons/fi";
import GeneralButton from "../../common/GeneralButton";
import axios from "../../../config/axios";
import { useGeneral } from "../../contexts/GeneralContext";
import { formatDateToReadable, isSnack } from "../../utils/commonHelper";
import CMSFormDate from "../../formFields/CMSFormDate";
import CMSFormDateRange from "../../formFields/CMSFormDateRange";

const SubscriptionDateChange = ({ meal }) => {
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [showModal, setShowModal] = useState(false);
  const [formValues, setFormValues] = useState(null);
  const { extraContent } = useGeneral();
  const { addToast } = useToast();
  const navigate = useNavigate();

  const refs = {
    reason: useRef(null),
    startDate: useRef(null),
  };

  const today = new Date().toLocaleDateString("en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  const scrollToFirstError = (errors) => {
    const firstErrorField = Object.keys(errors)[0];
    if (!firstErrorField || !refs[firstErrorField]?.current) return;

    const el = refs[firstErrorField].current;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    setTimeout(() => el.focus?.({ preventScroll: true }), 500);
  };

  const handleConfirm = async (form) => {
    setStatus("loading");

    try {
      // 🟢 API CALL TO UPDATE SUBSCRIPTION DATE
      await axios.post("/dashboard/subscriptions/reset-start-date", {
        subscriptionId: meal?._id,
        customerSelectedStartDate: formatDateToReadable(form.values?.startDate),
      });

      addToast(
        "Your meal subscription date has been updated successfully.",
        "success",
      );
      setStatus("success");
      setFormValues(null);

      form.resetForm();
      navigate(-1);
    } catch (err) {
      console.error("Date update failed:", err);
      addToast(
        err?.response?.data?.message ||
          "Failed to update the meal subscription date. Please try again.",
        "error",
      );
      setStatus("idle");
    } finally {
      setStatus("idle");
    }
  };

  return (
    <div className="mx-auto mt-8 p-6 lg:px-12 bg-[#F5F5F5] rounded-xl shadow-md relative">
      <div className="flex items-center gap-4 mb-6">
        <p className="text-24pt">Manage Subscription Date</p>
      </div>

      {status === "loading" && (
        <div className="absolute inset-0 flex items-center justify-center rounded-[15px] z-10 bg-white/70">
          <ContainerLoading />
        </div>
      )}

      {/* Meal Info Section */}
      <div className="grid lg:grid-cols-2 gap-4 lg:gap-8 mb-6">
        {/* Child Info */}
        <div className="bg-[#fff] flex items-center rounded-[15px] p-3">
          <div className="w-[56px] h-[56px] sm:w-[96px] sm:h-[96px] bg-white border border-[#DEE2DD] rounded-full flex items-center justify-center me-4">
            <Icon
              icon="profile"
              className={`w-[37px] h-[50px] sm:w-[57px] sm:h-[60px] ${
                meal?.kid?.gender?.toLowerCase() === "girl"
                  ? "text-[#FC05B0]/40"
                  : "text-[#0B81FD]/40"
              }`}
            />
          </div>
          <div className="flex flex-[1] flex-col items-start justify-start">
            <p className="text-21pt 2xl:text-24pt leading-light">
              {meal?.kid?.firstName} {meal?.kid?.lastName}
            </p>
            <p className="text-18pt">
              ({meal?.kid?.gender} - {meal?.kid?.age} years old)
            </p>
          </div>
          <div
            title="Click to edit child details"
            className="me-2 cursor-pointer"
            onClick={() => navigate("/account/profile/child-info")}
          >
            <Icon
              icon="edit"
              className="w-7 h-7 text-green hover:text-secondary"
            />
          </div>
        </div>

        {/* Plan Info */}
        <div className="bg-[#fff] flex items-center rounded-[15px] p-3">
          <div className="flex items-center justify-center me-4">
            <img
              src={mealImage}
              className="w-[100px] md:w-[90px] h-auto object-contain"
              alt="Meal"
            />
          </div>
          <div className="flex flex-col items-start justify-start xl:w-full">
            <p className="text-21pt 2xl:text-24pt leading-tight uppercase">
              {meal?.plan?.title} plan - ({meal?.totalMealsInPlan} days meals)
            </p>
            <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between flex-wrap xl:w-full">
              <p className="text-18pt text-quaternary me-2">
                {meal?.subscribedMeals?.length > 0
                  ? meal.subscribedMeals
                      .map((m) =>
                        isSnack(m?.meal?.title) ? "Snacks" : m?.meal?.title,
                      )
                      .join(" + ")
                  : "None selected"}
              </p>
              <p className="me-2">
                {" "}
                <span
                  className={`px-2 py-0.5 font-mono text-[12px] rounded-[5px] text-xs xl:whitespace-nowrap ${
                    meal?.vegOrNonVeg === "Veg"
                      ? "bg-quaternary text-white"
                      : "bg-orange text-secondary text-white"
                  }`}
                >
                  {meal?.vegOrNonVeg}
                </span>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Form Section */}
      <Formik
        initialValues={{ startDate: new Date(meal?.subscriptionStartDate) }}
        validationSchema={dateChangeSchema}
        onSubmit={() => {}}
      >
        {(form) => (
          <>
            <form
              className="flex flex-col gap-6"
              onSubmit={(e) => {
                e.preventDefault();
                form.validateForm().then((errors) => {
                  if (Object.keys(errors).length > 0) {
                    scrollToFirstError(errors);
                  } else {
                    setFormValues(form.values);
                    handleConfirm(form);
                  }
                });
              }}
            >
              <CMSFormDateRange
                name="startDate"
                ref={refs.startDate}
                label="Meal delivery start date"
                min={new Date(meal?.subscriptionStartDate)}
                createdAt={meal?.createdAt}
                allowedDays={extraContent?.mealSubscriptionStartDateAllowedDays}
                form={form}
                required
              />

              <div className="flex justify-end">
                <GeneralButton text="Submit" type="submit" />
              </div>
            </form>
          </>
        )}
      </Formik>
    </div>
  );
};

export default SubscriptionDateChange;